var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { createThemeScope } from '../theme/themeScope';
import { colorWithAlpha } from '../utils/color';
import { contrastingInk, parseCssPx, pickChrome, relativeLuminance, } from '../theme/themeUtils';
/** Dashboard widget palette — mirrors UI semantic token names for dark analytics panels. */
export var DASHBOARD = {
    panel: '#151d2e',
    panelStroke: '#2a3654',
    face: '#0f172a',
    text: '#e2e8f0',
    textMuted: '#94a3b8',
    textDim: '#64748b',
    /** Axis / legend label size (px). */
    fontSize: 12,
    fontSizeSm: 10,
    fontSizeTitle: 13,
    primary: '#3b82f6',
    secondary: '#ef4444',
    success: '#22c55e',
    warning: '#f59e0b',
    danger: '#ef4444',
    dangerDark: '#dc2626',
    inactive: '#374151',
    inactiveBar: '#475569',
    gaugeTrack: '#374151',
    gaugeNeedle: '#3b82f6',
    speedoNeedle: '#ef4444',
    chartBg: '#111827',
    chartGrid: '#334155',
    chartAxis: '#64748b',
    chartLine: '#3b82f6',
    chartArea: 'rgba(59, 130, 246, 0.35)',
    chartPlot: '#0f172a',
    chartTooltipBg: '#1e293b',
    chartTooltipBorder: '#475569',
    chartCrosshair: 'rgba(148, 163, 184, 0.6)',
    chartDot: '#60a5fa',
    barFill: '#3b82f6',
    compassFace: '#1c2740',
    compassRing: '#475569',
    compassHub: '#334155',
    thermometerTube: '#334155',
    thermometerBorder: '#475569',
    meterTrack: '#374151',
    meterFill: '#3b82f6',
    clockFace: '#1f2937',
    clockRing: '#374151',
    clockHand: '#e2e8f0',
    clockSecond: '#ef4444',
    batteryOutline: '#475569',
    batteryTip: '#475569',
    knobTrack: '#374151',
    knobRing: '#1f2937',
    knobIndicator: '#f59e0b',
    knobArc: 'rgba(245, 158, 11, 0.25)',
    clockTick: '#64748b',
    clockTickMajor: '#94a3b8',
    clockHub: '#374151',
    signalActive: '#22c55e',
    signalInactive: '#475569',
    pieStroke: '#1f2937',
    timelineLine: '#475569',
    timelineDot: '#3b82f6',
    highlight: '#3b82f6',
    series: ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#a855f7', '#06b6d4', '#f97316', '#ec4899'],
    financialUp: '#22c55e',
    financialDown: '#ef4444',
    flowLink: 'rgba(59, 130, 246, 0.45)',
    heatmapLow: '#1e3a5f',
    heatmapHigh: '#60a5fa',
};
function ensureReadableChartText(theme) {
    var _a, _b;
    var bg = theme.chartPlot || theme.chartBg || theme.panel;
    var bgLum = relativeLuminance(bg);
    var textLum = relativeLuminance(theme.text);
    if (bgLum == null || textLum == null)
        return theme;
    // Same-side contrast (both dark or both light) → fix text
    if ((bgLum < 0.45 && textLum < 0.45) || (bgLum > 0.6 && textLum > 0.6)) {
        var ink = contrastingInk(bg);
        return __assign(__assign({}, theme), { text: ink, textMuted: (_a = colorWithAlpha(ink, 0.7)) !== null && _a !== void 0 ? _a : theme.textMuted, chartAxis: (_b = colorWithAlpha(ink, 0.55)) !== null && _b !== void 0 ? _b : theme.chartAxis, clockHand: ink });
    }
    return theme;
}
function cloneDefaults() {
    return __assign(__assign({}, DASHBOARD), { series: __spreadArray([], __read(DASHBOARD.series), false) });
}
/** Merge optional UI theme tokens into the dashboard palette (for app-level theming). */
export function resolveDashboardTheme(ui, pack) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
    if ((!ui || Object.keys(ui).length === 0) && (!pack || Object.keys(pack).length === 0)) {
        return cloneDefaults();
    }
    var primary = (_a = ui === null || ui === void 0 ? void 0 : ui.primary) !== null && _a !== void 0 ? _a : DASHBOARD.primary;
    var warning = (_b = ui === null || ui === void 0 ? void 0 : ui.warning) !== null && _b !== void 0 ? _b : DASHBOARD.warning;
    var success = (_c = ui === null || ui === void 0 ? void 0 : ui.success) !== null && _c !== void 0 ? _c : DASHBOARD.success;
    var danger = (_d = ui === null || ui === void 0 ? void 0 : ui.danger) !== null && _d !== void 0 ? _d : DASHBOARD.danger;
    var chartArea = (_e = colorWithAlpha(primary, 0.35)) !== null && _e !== void 0 ? _e : DASHBOARD.chartArea;
    var flowLink = (_f = colorWithAlpha(primary, 0.45)) !== null && _f !== void 0 ? _f : DASHBOARD.flowLink;
    var knobArc = (_g = colorWithAlpha(warning, 0.25)) !== null && _g !== void 0 ? _g : DASHBOARD.knobArc;
    var base = !ui || Object.keys(ui).length === 0
        ? cloneDefaults()
        : __assign(__assign({}, cloneDefaults()), { primary: primary, secondary: (_h = ui.secondary) !== null && _h !== void 0 ? _h : DASHBOARD.secondary, success: success, warning: warning, danger: danger, text: (_j = ui.text) !== null && _j !== void 0 ? _j : DASHBOARD.text, textMuted: (_k = ui.textMuted) !== null && _k !== void 0 ? _k : DASHBOARD.textMuted, textDim: (_l = ui.textMuted) !== null && _l !== void 0 ? _l : DASHBOARD.textDim, fontSize: parseCssPx(ui.fontSize, DASHBOARD.fontSize), fontSizeSm: parseCssPx(ui.fontSizeSm, DASHBOARD.fontSizeSm), fontSizeTitle: parseCssPx(ui.fontSizeLg, DASHBOARD.fontSizeTitle), panel: pickChrome(ui.surface, DASHBOARD.panel), panelStroke: pickChrome(ui.border, DASHBOARD.panelStroke), face: pickChrome(ui.surfaceMuted, DASHBOARD.face), 
            // Chart chrome follows dark surfaces; light UI packs keep analytics defaults
            chartBg: pickChrome(ui.surfaceMuted, DASHBOARD.chartBg), chartPlot: pickChrome(ui.surfaceMuted, DASHBOARD.chartPlot), chartGrid: pickChrome(ui.border, DASHBOARD.chartGrid), chartAxis: (_m = ui.textMuted) !== null && _m !== void 0 ? _m : DASHBOARD.chartAxis, chartTooltipBg: pickChrome(ui.surface, DASHBOARD.chartTooltipBg), chartTooltipBorder: pickChrome(ui.borderStrong, DASHBOARD.chartTooltipBorder), gaugeTrack: pickChrome(ui.surfaceInset, DASHBOARD.gaugeTrack), meterTrack: pickChrome(ui.surfaceInset, DASHBOARD.meterTrack), inactive: pickChrome(ui.borderStrong, DASHBOARD.inactive), inactiveBar: pickChrome(ui.border, DASHBOARD.inactiveBar), thermometerTube: pickChrome(ui.surfaceInset, DASHBOARD.thermometerTube), thermometerBorder: pickChrome(ui.border, DASHBOARD.thermometerBorder), compassFace: pickChrome(ui.surface, DASHBOARD.compassFace), compassRing: pickChrome(ui.border, DASHBOARD.compassRing), compassHub: pickChrome(ui.surfaceInset, DASHBOARD.compassHub), clockFace: pickChrome(ui.surface, DASHBOARD.clockFace), clockRing: pickChrome(ui.border, DASHBOARD.clockRing), clockHand: (_o = ui.text) !== null && _o !== void 0 ? _o : DASHBOARD.clockHand, clockTick: (_p = ui.textMuted) !== null && _p !== void 0 ? _p : DASHBOARD.clockTick, clockTickMajor: (_q = ui.textSecondary) !== null && _q !== void 0 ? _q : DASHBOARD.clockTickMajor, clockHub: pickChrome(ui.surfaceInset, DASHBOARD.clockHub), batteryOutline: pickChrome(ui.borderStrong, DASHBOARD.batteryOutline), batteryTip: pickChrome(ui.borderStrong, DASHBOARD.batteryTip), knobTrack: pickChrome(ui.surfaceInset, DASHBOARD.knobTrack), knobRing: pickChrome(ui.surfaceMuted, DASHBOARD.knobRing), signalInactive: pickChrome(ui.borderStrong, DASHBOARD.signalInactive), pieStroke: pickChrome(ui.surface, DASHBOARD.pieStroke), timelineLine: pickChrome(ui.border, DASHBOARD.timelineLine), heatmapLow: pickChrome((_r = ui.primarySubtle) !== null && _r !== void 0 ? _r : ui.surfaceInset, DASHBOARD.heatmapLow), gaugeNeedle: primary, chartLine: primary, barFill: primary, meterFill: primary, highlight: primary, timelineDot: primary, chartArea: chartArea, chartDot: primary, flowLink: flowLink, heatmapHigh: primary, signalActive: success, financialUp: success, financialDown: danger, speedoNeedle: danger, clockSecond: danger, knobIndicator: warning, knobArc: knobArc, series: [primary, DASHBOARD.series[1], DASHBOARD.series[2], DASHBOARD.series[3]] });
    if (!pack || Object.keys(pack).length === 0) {
        return ensureReadableChartText(base);
    }
    var next = __assign(__assign(__assign({}, base), pack), { series: ((_s = pack.series) === null || _s === void 0 ? void 0 : _s.length) ? __spreadArray([], __read(pack.series), false) : __spreadArray([], __read(base.series), false), fontSize: parseCssPx((_t = pack.fontSize) !== null && _t !== void 0 ? _t : base.fontSize, base.fontSize), fontSizeSm: parseCssPx((_u = pack.fontSizeSm) !== null && _u !== void 0 ? _u : base.fontSizeSm, base.fontSizeSm), fontSizeTitle: parseCssPx((_v = pack.fontSizeTitle) !== null && _v !== void 0 ? _v : base.fontSizeTitle, base.fontSizeTitle) });
    return ensureReadableChartText(next);
}
var dashboardScope = createThemeScope(cloneDefaults);
/** Module pack (dashboard + series) stored on the App theme. */
export function dashboardPackFromApp(app) {
    var _a, _b;
    if (!app || typeof app.getTheme !== 'function')
        return {};
    var t = app.getTheme();
    return __assign(__assign({}, ((_a = t.dashboard) !== null && _a !== void 0 ? _a : {})), (((_b = t.series) === null || _b === void 0 ? void 0 : _b.length) ? { series: __spreadArray([], __read(t.series), false) } : {}));
}
/** Sync App-scoped dashboard palette (App init / setUiTheme / create). */
export function syncActiveDashboardTheme(tokens, app, pack) {
    if (tokens === void 0) { tokens = {}; }
    return dashboardScope.sync(resolveDashboardTheme(tokens, pack), app !== null && app !== void 0 ? app : undefined);
}
/**
 * Current dashboard palette for builders.
 * Prefer calling inside a create/rebuild (stack); optional `app` uses WeakMap snapshot.
 */
export function getActiveDashboard(app) {
    return dashboardScope.getActive(app !== null && app !== void 0 ? app : undefined);
}
/** Run factory/rebuild with a fixed palette on the build stack. */
export function runWithDashboardTheme(theme, fn) {
    return dashboardScope.runWithResult(theme, fn);
}
/** Resolve palette for an app without mutating the active theme. */
export function getDashboardTheme(app) {
    if (app && typeof app.getResolvedTheme === 'function') {
        return resolveDashboardTheme(app.getResolvedTheme(), dashboardPackFromApp(app));
    }
    return cloneDefaults();
}
/** Rebuild every dashboard widget that exposes `chartRebuild` (gauges + charts). */
export function refreshDashboard(root, _app) {
    var visit = function (n) {
        var e_1, _a;
        var _b, _c;
        var rebuild = (_b = n.metadata) === null || _b === void 0 ? void 0 : _b.chartRebuild;
        if (typeof rebuild === 'function' && ((_c = n.metadata) === null || _c === void 0 ? void 0 : _c.widgetType)) {
            // Per-node uiTheme applied inside chartRebuild via resolveEffectiveUiTokens
            rebuild();
        }
        if ('children' in n) {
            try {
                for (var _d = __values(n.children), _e = _d.next(); !_e.done; _e = _d.next()) {
                    var child = _e.value;
                    visit(child);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
    };
    visit(root);
}
